import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GiftIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useRewardCatalog, useRedeemReward } from '@/hooks/useRewards'
import type { RewardCatalogItem, RedemptionResult } from '@/types'

// ---------------------------------------------------------------------------
// Catalog item card
// ---------------------------------------------------------------------------

interface CatalogItemCardProps {
  item: RewardCatalogItem
  onRedeem: (item: RewardCatalogItem) => void
  isRedeeming: boolean
}

function CatalogItemCard({ item, onRedeem, isRedeeming }: CatalogItemCardProps) {
  const { t } = useTranslation('rewards')

  const isDisabled = !item.affordable || (item.stock !== null && item.stock <= 0) || isRedeeming

  return (
    <div className="flat-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-base-content leading-snug">{item.name}</p>
          {item.thb_value != null && (
            <p className="text-xs text-base-content/60 mt-0.5">
              {t('thb_value', { amount: item.thb_value.toFixed(2) })}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-sm font-bold text-primary">
            {t('cost_points', { points: item.cost_points.toLocaleString() })}
          </p>
          {item.stock !== null && item.stock <= 0 && (
            <span className="text-xs text-error">{t('out_of_stock')}</span>
          )}
        </div>
      </div>

      <button
        className="btn btn-primary btn-sm w-full"
        disabled={isDisabled}
        onClick={() => onRedeem(item)}
        aria-label={t('redeem_button')}
      >
        {item.stock !== null && item.stock <= 0
          ? t('out_of_stock')
          : !item.affordable
            ? t('not_enough_points')
            : t('redeem_button')}
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Confirm modal
// ---------------------------------------------------------------------------

interface ConfirmModalProps {
  item: RewardCatalogItem
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
}

function ConfirmModal({ item, onConfirm, onCancel, isPending }: ConfirmModalProps) {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pointer-events-auto"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={onCancel} />

      {/* Panel */}
      <div className="relative z-10 bg-base-100 rounded-t-2xl sm:rounded-2xl w-full max-w-[430px] p-6 space-y-4">
        <h2 className="text-base font-bold text-base-content">{t('redeem_confirm_title')}</h2>
        <p className="text-sm text-base-content/80">
          {t('redeem_confirm_body', {
            name: item.name,
            points: item.cost_points.toLocaleString(),
          })}
        </p>
        <div className="flex gap-3 pt-2">
          <button
            className="btn btn-ghost flex-1"
            onClick={onCancel}
            disabled={isPending}
          >
            {tc('actions.cancel')}
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? tc('status.loading') : t('redeem_button')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Success card
// ---------------------------------------------------------------------------

interface SuccessCardProps {
  result: RedemptionResult
  onDismiss: () => void
}

function SuccessCard({ result, onDismiss }: SuccessCardProps) {
  const { t } = useTranslation('rewards')

  return (
    <div className="flat-card p-5 flex flex-col items-center gap-3 text-center border-2 border-success">
      <div className="icon-container bg-success/10">
        <GiftIcon className="w-5 h-5 text-success" />
      </div>
      <p className="text-sm font-bold text-base-content">{t('redeem_success_title')}</p>
      <p className="text-xs text-base-content/80">
        {t('redeem_success_body', { name: result.reward_name })}
      </p>
      {result.fulfillment_ref && (
        <p className="text-xs font-mono text-base-content/60">
          {t('fulfillment_ref', { ref: result.fulfillment_ref })}
        </p>
      )}
      <button className="btn btn-sm btn-ghost" onClick={onDismiss}>
        OK
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ErrorCode = 'insufficient_points' | 'reward_unavailable' | 'no_active_lease' | 'redeem_failed'

export default function RewardCatalogPage() {
  const { t } = useTranslation('rewards')
  const { t: tc } = useTranslation('common')
  const { data: items, isLoading, error } = useRewardCatalog()
  const { mutate: redeem, isPending } = useRedeemReward()

  const [confirmItem, setConfirmItem] = useState<RewardCatalogItem | null>(null)
  const [successResult, setSuccessResult] = useState<RedemptionResult | null>(null)
  const [redeemError, setRedeemError] = useState<string | null>(null)

  if (isLoading) return <LoadingSpinner />

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">{tc('errors.something_went_wrong')}</div>
      </div>
    )
  }

  const handleRedeemClick = (item: RewardCatalogItem) => {
    setRedeemError(null)
    setSuccessResult(null)
    setConfirmItem(item)
  }

  const handleConfirm = () => {
    if (!confirmItem) return

    redeem(
      {
        reward_catalog_item_id: confirmItem.id,
        idempotency_key: crypto.randomUUID(),
      },
      {
        onSuccess: (result) => {
          setConfirmItem(null)
          setSuccessResult(result)
        },
        onError: (err) => {
          setConfirmItem(null)
          // Try to parse the error code from the response
          const apiError = (err as { response?: { data?: { error?: string } } }).response?.data
            ?.error as ErrorCode | undefined
          const messageKey: ErrorCode = apiError ?? 'redeem_failed'
          setRedeemError(t(`errors.${messageKey}`))
        },
      },
    )
  }

  return (
    <div className="p-4 space-y-3">
      {/* Success result card */}
      {successResult && (
        <SuccessCard result={successResult} onDismiss={() => setSuccessResult(null)} />
      )}

      {/* Error banner */}
      {redeemError && (
        <div className="alert alert-error text-sm">{redeemError}</div>
      )}

      {/* Empty state */}
      {!isLoading && items?.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <div className="flex justify-center">
            <div className="icon-container">
              <GiftIcon className="w-5 h-5" />
            </div>
          </div>
          <p className="text-sm text-base-content/60">{t('no_catalog')}</p>
        </div>
      )}

      {/* Catalog items */}
      {items?.map((item) => (
        <CatalogItemCard
          key={item.id}
          item={item}
          onRedeem={handleRedeemClick}
          isRedeeming={isPending}
        />
      ))}

      {/* Confirm modal */}
      {confirmItem && (
        <ConfirmModal
          item={confirmItem}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmItem(null)}
          isPending={isPending}
        />
      )}
    </div>
  )
}
